/**
 * BatchListTab — one server-paged BMR Details list (EXV-008 / EXV-032).
 *
 * The tab owns its read: `load(page)` resolves one page ({ rows, total, pageSize }) or rejects. A failure shows the
 * "couldn't load" state with Retry and status-specific words — never an empty or stale list; an empty page names
 * what is empty (`emptyMessage`). The page size is the server's. A Refresh control and the time of the last read
 * sit in the list toolbar. `reloadKey` re-reads page 1 (filters changed, an act was signed).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { RefreshCw } from 'lucide-react';
import DataTable from '@/components/DataTable';
import { formatDateTime } from '@/utils/datetime';
import { failureMessage } from './bmrDetailsApi';

export default function BatchListTab({
  load,
  reloadKey,
  columns,
  emptyMessage,
  what,
  rowKey = 'batch_number',
  toolbar,
  filterContent,
  filterActiveCount,
  onFilterClear,
  testId,
  tableProps = {},
  onRows,
}) {
  const [state, setState] = useState({ rows: [], total: 0, pageSize: undefined, loading: true, error: null, at: null });
  const [page, setPage] = useState(1);
  const seq = useRef(0);

  const read = useCallback(
    (p) => {
      const id = ++seq.current;
      setState((s) => ({ ...s, loading: true, error: null }));
      load(p)
        .then((r) => {
          if (id !== seq.current) return;
          setState({ rows: r.rows, total: r.total, pageSize: r.pageSize, loading: false, error: null, at: new Date() });
          if (onRows) onRows(r.rows);
        })
        .catch((err) => {
          if (id !== seq.current) return;
          setState((s) => ({ ...s, rows: [], total: 0, loading: false, error: err || true, at: null }));
        });
    },
    [load, onRows]
  );

  // a new filter set (or an act that changed the list) starts again at page 1
  useEffect(() => {
    setPage(1);
    read(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const refresh = () => read(page);

  return (
    <div className="bmrd-card" data-testid={testId}>
      <div className="bmrd-list">
        <DataTable
          serverSide
          tableKey={`bmrd-${testId}`}
          columnConfig
          filterPanel
          filterContent={filterContent}
          filterActiveCount={filterActiveCount}
          onFilterClear={onFilterClear}
          showCount={false}
          bordered
          height={null}
          minHeight={320}
          rowKey={rowKey}
          toolbarExtra={
            <>
              {toolbar}
              <span className="bmrd-updated" aria-live="polite">
                {state.at ? `Updated ${formatDateTime(state.at, 'HH:mm:ss')}` : ''}
              </span>
            </>
          }
          onRefresh={refresh}
          loading={state.loading}
          data={state.rows}
          columns={columns}
          emptyMessage={emptyMessage}
          error={state.error ? true : undefined}
          errorMessage={state.error ? failureMessage(state.error, what) : undefined}
          onRetry={refresh}
          pagination={!state.pageSize ? false : {
            current: page,
            pageSize: state.pageSize,
            total: state.total,
            showSizeChanger: false,
            onChange: (p) => {
              setPage(p);
              read(p);
            },
          }}
          {...tableProps}
        />
      </div>
    </div>
  );
}
